import { optimizeImage } from "@/utils/optimizeImage";
import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Pause, Play } from "lucide-react";


const GALLERY = [
  // Recent installs — Feb 2026 batch (Blink security + UniFi networking)
  {
    id: "unifi-ap-ceiling-install",
    src: "/portfolio/ubiquiti-unifi-access-point-ceiling-install-luxury-home-swfl.webp",
    alt: "Ubiquiti UniFi access point flush-mounted to luxury home ceiling for whole-home WiFi coverage in Naples, FL",
    span: "md:col-span-1",
  },
  {
    id: "blink-soffit-camera",
    src: "/portfolio/blink-outdoor-camera-soffit-mount-naples-residential.webp",
    alt: "Blink outdoor security camera professionally installed under residential soffit at SWFL home",
    span: "md:col-span-1",
  },
  {
    id: "blink-tree-camera",
    src: "/portfolio/blink-security-camera-tree-mount-property-perimeter-swfl.webp",
    alt: "Blink security camera tree-mounted for property perimeter surveillance in Naples, FL",
    span: "md:col-span-1",
  },
  {
    id: "blink-camera-prep",
    src: "/portfolio/blink-camera-system-prep-whole-home-security-naples.webp",
    alt: "Blink whole-property security camera system prepped for multi-zone install in SWFL residence",
    span: "md:col-span-1",
  },
  {
    id: "outdoor-cam-ladder-install",
    src: "/portfolio/outdoor-security-camera-installation-pine-tree-naples-fl.webp",
    alt: "I.T. Help of SWFL technician installing outdoor security camera high in pine tree at Naples property",
    span: "md:col-span-2 md:row-span-2",
  },
  // User-uploaded images (migrated from customer-assets CDN to local optimized WebPs)
  {
    id: "new-construction-mount",
    src: "/portfolio/new-construction-wireless-tv-mount-naples-fl.webp",
    alt: "Wireless TV mounting in a new construction with no cables shown in Naples, FL",
    span: "md:col-span-2 md:row-span-2",
  },
  {
    id: "wall-mount-holiday",
    src: "/portfolio/wall-mounted-tv-holiday-stroll-bonita-springs.webp",
    alt: "Wall-mounted TV displaying local Holiday Stroll event in Bonita Springs",
    span: "md:col-span-1",
  },
  {
    id: "flush-mount-bedroom",
    src: "/portfolio/flush-mount-tv-bedroom-cable-free-naples.webp",
    alt: "Flush wall-mounted flat-screen TV with clean cable-free installation",
    span: "md:col-span-1",
  },
  {
    id: "smart-tv-setup",
    src: "/portfolio/wall-mounted-smart-tv-inside-out-streaming.webp",
    alt: "Wall-mounted smart TV displaying Inside Out 2 with streaming apps",
    span: "md:col-span-1",
  },
  {
    id: "bedroom-tv-install",
    src: "/portfolio/bedroom-tv-mounting-above-cabinet-naples.webp",
    alt: "Clean bedroom TV mounting above white cabinet with plant decor",
    span: "md:col-span-1",
  },
  // From original website gallery (migrated from Squarespace CDN to local optimized WebPs)
  {
    id: "lg-sonos-mount",
    src: "/portfolio/lg-sonos-beam-tv-mounting-naples.webp",
    alt: "Wireless TV mounting with LG TV and SONOS Beam Soundbar with flush mount in Naples",
    span: "md:col-span-2 md:row-span-2",
  },
  {
    id: "server-rack",
    src: "/portfolio/server-rack-network-installation-swfl.webp",
    alt: "Server rack with interconnected network hardware, switches, routers, and patch panels",
    span: "md:col-span-1",
  },
  {
    id: "lg-oled-xfinity",
    src: "/portfolio/lg-oled-tv-mounting-xfinity-naples.webp",
    alt: "Wireless TV mounting with LG OLED TV and Xfinity modem in Naples",
    span: "md:col-span-1",
  },
  {
    id: "standing-desk",
    src: "/portfolio/adjustable-standing-desk-assembly-naples.webp",
    alt: "Adjustable standing desk assembled by I.T. Help of SWFL in Naples",
    span: "md:col-span-1",
  },
  {
    id: "tv-above-stand",
    src: "/portfolio/wireless-tv-mount-above-stand-naples.webp",
    alt: "Wireless TV mount with no cables shown above a TV stand in Naples",
    span: "md:col-span-1",
  },
  {
    id: "outlet-tv-mount",
    src: "/portfolio/recessed-outlet-tv-mount-naples.webp",
    alt: "Electrical outlet installed above a TV mount for a wireless smart home experience",
    span: "md:col-span-1",
  },
  {
    id: "ring-camera",
    src: "/portfolio/ring-floodlight-camera-naples-fl.webp",
    alt: "Ring floodlight camera installed on concrete exterior wall in Naples",
    span: "md:col-span-1",
  },
  {
    id: "digital-signage-wu",
    src: "/portfolio/western-union-digital-signage-naples.webp",
    alt: "Digital signage screen installed for a Western Union store in Naples",
    span: "md:col-span-1",
  },
  {
    id: "gaming-setup",
    src: "/portfolio/gaming-setup-triple-monitor-racing-rig-naples.webp",
    alt: "Wall-mounted TV with gaming setup featuring three monitors and racing wheel",
    span: "md:col-span-2 md:row-span-2",
  },
  {
    id: "gallery-spotlight",
    src: "/portfolio/gallery-tv-wall-mount-spotlight-naples.webp",
    alt: "Gallery-style TV wall mounting with zero visible cables under custom spotlighting",
    span: "md:col-span-1",
  },
  {
    id: "stone-wall-mount",
    src: "/portfolio/stone-wall-tv-mounting-naples.webp",
    alt: "Living room with mounted TV on stone accent wall",
    span: "md:col-span-1",
  },
  {
    id: "mantle-mount",
    src: "/portfolio/tv-mount-above-mantle-naples.webp",
    alt: "Wall-mounted flat-screen TV above a white mantle in Naples home",
    span: "md:col-span-1",
  },
  {
    id: "lift-cabinet",
    src: "/portfolio/motorized-lift-cabinet-tv-install-naples.webp",
    alt: "Custom lift cabinet TV installation with hidden wiring and motorized lift",
    span: "md:col-span-1",
  },
  {
    id: "gaming-pc",
    src: "/portfolio/custom-gaming-pc-liquid-cooling-build-swfl.webp",
    alt: "Custom-built gaming PC with liquid cooling, RTX GPU, and RGB lighting",
    span: "md:col-span-1",
  },
  {
    id: "drive-thru-signage",
    src: "/portfolio/drive-thru-digital-menu-boards-swfl.webp",
    alt: "Drive-thru coffee shop with professional digital menu boards installation",
    span: "md:col-span-2",
  },
  {
    id: "rgb-pc-build",
    src: "/portfolio/rgb-pc-build-radeon-gpu-swfl.webp",
    alt: "Inside a computer case with RGB lighting, Radeon graphics card, and cooling fans",
    span: "md:col-span-1",
  },
  {
    id: "tv-news-display",
    src: "/portfolio/tv-news-display-client-home-naples.webp",
    alt: "Television screen displaying news broadcast in Naples client home",
    span: "md:col-span-1",
  },
];

export default function Gallery() {
  const [page, setPage] = useState(0);
  const [paused, setPaused] = useState(() => window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [visible, setVisible] = useState(false);
  const section = useRef(null);
  const count = Math.ceil(GALLERY.length / 4);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting), { threshold: 0.1 });
    if (section.current) observer.observe(section.current);
    const preference = window.matchMedia("(prefers-reduced-motion: reduce)");
    const stop = () => { if (preference.matches) setPaused(true); };
    preference.addEventListener("change", stop);
    return () => { observer.disconnect(); preference.removeEventListener("change", stop); };
  }, []);
  useEffect(() => {
    if (paused || hovered || focused || !visible) return;
    const interval = setInterval(() => setPage((p) => (p + 1) % count), 6500);
    return () => clearInterval(interval);
  }, [paused, hovered, focused, visible, count]);
  const move = (direction) => { setPaused(true); setPage((p) => (p + direction + count) % count); };

  return (
    <section ref={section} id="portfolio" data-testid="portfolio-section" className="py-24 md:py-32" aria-label="Portfolio Gallery">
      <div className="max-w-7xl mx-auto px-6 md:px-12 mb-10">
        <p className="text-xs font-bold tracking-[0.2em] uppercase text-blue-400 mb-4">
          Our Work
        </p>
        <h2
          className="text-3xl md:text-4xl tracking-tight font-medium mb-3"
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          Real Installations, Real Results
        </h2>
        <p className="text-slate-400 text-sm md:text-base max-w-xl">
          Recent installations across Southwest Florida.
        </p>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12" data-testid="portfolio-marquee" role="region" aria-label="Installation photos" aria-roledescription="carousel"
        onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)} onFocusCapture={() => setFocused(true)} onBlurCapture={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setFocused(false); }}>
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <button type="button" onClick={() => setPaused(!paused)} aria-pressed={paused} className="inline-flex gap-2 items-center min-h-12 border border-white/20 rounded-lg px-4 hover:bg-white/5" data-testid="portfolio-pause">{paused ? <Play size={18} /> : <Pause size={18} />}{paused ? "Play slideshow" : "Pause slideshow"}</button>
          <button type="button" onClick={() => move(-1)} aria-label="Previous installation photos" className="p-3 rounded-lg border border-white/20" data-testid="portfolio-previous"><ChevronLeft size={20} /></button>
          <button type="button" onClick={() => move(1)} aria-label="Next installation photos" className="p-3 rounded-lg border border-white/20" data-testid="portfolio-next"><ChevronRight size={20} /></button>
          <p className="text-sm text-slate-300" aria-live={paused ? "polite" : "off"} data-testid="portfolio-page">{page * 4 + 1}–{Math.min(page * 4 + 4, GALLERY.length)} of {GALLERY.length}</p>
        </div>
        <div className="grid grid-cols-2 grid-rows-2 md:grid-cols-4 md:grid-rows-1 gap-3 aspect-square md:aspect-[4/1]">
          {GALLERY.slice(page * 4, page * 4 + 4).map((item) => (
            <div
              key={item.id}
              data-testid={`gallery-item-${item.id}`}
              className="relative min-w-0 min-h-0 rounded-lg bg-white/[0.03]"
            >
              <img
                src={optimizeImage(item.src, { width: 800 })}
                alt={item.alt}
                width="360"
                height="260"
                loading="lazy"
                fetchPriority="low"
                decoding="async"
                className="w-full h-full object-contain rounded-lg"
              />
            </div>
          ))}
        </div>
      </div>
      <div className="mt-10 text-center">
        <a
          href="/portfolio/port-royal-estate-automation"
          data-testid="portfolio-case-studies"
          className="inline-flex items-center min-h-[44px] gap-2 text-sm text-blue-300 hover:text-blue-200 font-medium px-4 py-2.5"
        >
          Read featured case studies →
        </a>
      </div>
    </section>
  );
}
